import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { ImageUploadField } from '@/components/ui/image-upload-field'
import { SettingsRow } from '@/components/settings/settings-row'
import { SettingsImageUploadRow } from '@/components/settings/settings-image-upload-row'
import { getPersonDisplayMeta } from '@/lib/person-identity-display'

export type PersonIdentityFieldsValue = {
  name: string
  greeting_name: string
}

export type PersonIdentityFieldsLayout = 'settings' | 'wizard'

type PersonIdentityFieldsProps = {
  layout: PersonIdentityFieldsLayout
  value: PersonIdentityFieldsValue
  onChange: (patch: Partial<PersonIdentityFieldsValue>) => void
  avatarUrl?: string | null
  avatarColorSeed?: string
  uploadAvatar?: (file: File) => Promise<unknown>
  deleteAvatar?: () => Promise<unknown>
  isUploadingAvatar?: boolean
  isDeletingAvatar?: boolean
  uploadAvatarError?: unknown
  disabled?: boolean
  nameDescription?: string
  greetingDescription?: string
}

function AvatarBlock({
  avatarColorSeed,
  avatarUrl,
  uploadAvatar,
  deleteAvatar,
  isUploadingAvatar,
  isDeletingAvatar,
  uploadAvatarError,
  disabled,
  label = 'Profile photo',
  description,
}: {
  avatarColorSeed: string
  avatarUrl?: string | null
  uploadAvatar?: (file: File) => Promise<unknown>
  deleteAvatar?: () => Promise<unknown>
  isUploadingAvatar?: boolean
  isDeletingAvatar?: boolean
  uploadAvatarError?: unknown
  disabled?: boolean
  label?: string
  description?: string
}) {
  const errorMessage =
    uploadAvatarError instanceof Error
      ? uploadAvatarError.message
      : uploadAvatarError
        ? String(uploadAvatarError)
        : undefined

  return (
    <FormField label={label} description={description} error={errorMessage}>
      <ImageUploadField
        variant="avatar"
        avatarColorSeed={avatarColorSeed}
        serverImageUrl={avatarUrl}
        uploadAvatar={uploadAvatar}
        deleteAvatar={deleteAvatar}
        isUploadingAvatar={isUploadingAvatar}
        isDeletingAvatar={isDeletingAvatar}
        disabled={disabled}
        persistenceNote={null}
      />
    </FormField>
  )
}

function NameFields({
  value,
  onChange,
  disabled,
  nameDescription,
  greetingDescription,
  idPrefix = 'person-identity',
}: {
  value: PersonIdentityFieldsValue
  onChange: (patch: Partial<PersonIdentityFieldsValue>) => void
  disabled?: boolean
  nameDescription?: string
  greetingDescription?: string
  idPrefix?: string
}) {
  return (
    <>
      <FormField
        label="Full name"
        htmlFor={`${idPrefix}-name`}
        description={nameDescription}
      >
        <Input
          id={`${idPrefix}-name`}
          value={value.name}
          onChange={(event) => onChange({ name: event.target.value })}
          autoComplete="name"
          placeholder="Your full name"
          disabled={disabled}
        />
      </FormField>
      <FormField
        label="Preferred name"
        htmlFor={`${idPrefix}-greeting`}
        description={greetingDescription ?? 'Optional — used for casual greetings in the app.'}
      >
        <Input
          id={`${idPrefix}-greeting`}
          value={value.greeting_name}
          onChange={(event) => onChange({ greeting_name: event.target.value })}
          autoComplete="nickname"
          placeholder="What should we call you?"
          disabled={disabled}
        />
      </FormField>
    </>
  )
}

/** Shared account-owned identity fields (name, preferred name, avatar). */
export function PersonIdentityFields({
  layout,
  value,
  onChange,
  avatarUrl,
  avatarColorSeed,
  uploadAvatar,
  deleteAvatar,
  isUploadingAvatar,
  isDeletingAvatar,
  uploadAvatarError,
  disabled,
  nameDescription,
  greetingDescription,
}: PersonIdentityFieldsProps) {
  const seed =
    avatarColorSeed ||
    getPersonDisplayMeta({ name: value.name, greeting_name: value.greeting_name }).colorSeed ||
    'profile'

  if (layout === 'settings') {
    return (
      <>
        <SettingsImageUploadRow label="Photo">
          <ImageUploadField
            variant="avatar"
            avatarColorSeed={seed}
            serverImageUrl={avatarUrl}
            uploadAvatar={uploadAvatar}
            deleteAvatar={deleteAvatar}
            isUploadingAvatar={isUploadingAvatar}
            isDeletingAvatar={isDeletingAvatar}
            disabled={disabled}
            persistenceNote={null}
          />
        </SettingsImageUploadRow>
        <SettingsRow label="Full name">
          <Input
            id="settings-person-name"
            aria-label="Full name"
            value={value.name}
            onChange={(event) => onChange({ name: event.target.value })}
            autoComplete="name"
            className="h-8 w-full text-sm sm:max-w-[320px]"
            placeholder="Full name"
            disabled={disabled}
          />
        </SettingsRow>
        <SettingsRow label="Preferred name" showDivider={false}>
          <Input
            id="settings-person-greeting"
            aria-label="Preferred name"
            value={value.greeting_name}
            onChange={(event) => onChange({ greeting_name: event.target.value })}
            autoComplete="nickname"
            className="h-8 w-full text-sm sm:max-w-[320px]"
            placeholder="What should we call you?"
            disabled={disabled}
          />
        </SettingsRow>
      </>
    )
  }

  return (
    <div className="space-y-6">
      <AvatarBlock
        avatarColorSeed={seed}
        avatarUrl={avatarUrl}
        uploadAvatar={uploadAvatar}
        deleteAvatar={deleteAvatar}
        isUploadingAvatar={isUploadingAvatar}
        isDeletingAvatar={isDeletingAvatar}
        uploadAvatarError={uploadAvatarError}
        disabled={disabled}
        description={
          uploadAvatarError
            ? undefined
            : 'Optional — add a photo now or continue and add one later.'
        }
      />
      <NameFields
        value={value}
        onChange={onChange}
        disabled={disabled}
        nameDescription={nameDescription}
        greetingDescription={greetingDescription}
        idPrefix="wizard-person-identity"
      />
    </div>
  )
}
